// Created By Cursor
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  Language as WebsiteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  OpenInNew as OpenIcon,
  School as SchoolIcon,
  Article as AboutIcon,
  ViewCarousel as BannerIcon,
  Collections as GalleryIcon,
  Description as DocumentsIcon,
  RateReview as TestimonialsIcon,
  Campaign as NewsIcon,
  Groups as StaffIcon,
  EmojiEvents as ToppersIcon,
  Apartment as InfraIcon,
  Payments as FeeIcon,
  Inbox as EnquiryIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  Public as DomainIcon,
  Badge as BadgeIcon,
  Visibility as VisionIcon,
  Flag as MissionIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import { crmColors } from "../theme/colors";
import websiteApiService from "../services/websiteApiService";

const BRAND = crmColors.primary.main;
const ACCENT = crmColors.energy.main;

const MODULE_META = {
  about: { label: "About", icon: AboutIcon, color: BRAND },
  banners: { label: "Banners", icon: BannerIcon, color: crmColors.info.light },
  details: { label: "Details", icon: SettingsIcon, color: "#0d9488" },
  gallery: { label: "Gallery", icon: GalleryIcon, color: "#7c3aed" },
  documents: { label: "Documents", icon: DocumentsIcon, color: crmColors.warning.main },
  testimonials: { label: "Testimonials", icon: TestimonialsIcon, color: crmColors.energy.main },
  message_desk: { label: "Messages", icon: NewsIcon, color: crmColors.success.main },
  staff: { label: "Staff", icon: StaffIcon, color: "#475569" },
  toppers: { label: "Toppers", icon: ToppersIcon, color: "#ca8a04" },
  infrastructure: { label: "Infrastructure", icon: InfraIcon, color: "#0369a1" },
  fee: { label: "Fees", icon: FeeIcon, color: "#be123c" },
  enquiry: { label: "Enquiries", icon: EnquiryIcon, color: crmColors.primary.dark },
  summary: { label: "Summary", icon: SchoolIcon, color: "#64748b" },
  values: { label: "Values", icon: AboutIcon, color: "#0f766e" },
};

const emptyForm = () => ({
  school_name: "",
  school_address: "",
  country: "",
  state: "",
  admin_name: "",
  admin_phone: "",
  domain_name: "",
  status: "Active",
  school_tag_line: "",
  school_heading: "",
  school_estb_year: "",
  affiliated_to: "",
  school_reg_no: "",
  affi_no: "",
  website: "",
  official_contact_no1: "",
  official_contact_no2: "",
  official_email: "",
  principal_name: "",
  principal_qualification: "",
  about_title: "",
  about_heading: "",
  about_school: "",
  mission_title: "",
  mission_heading: "",
  school_mission: "",
  vision_title: "",
  vision_heading: "",
  school_vision: "",
});

const publicSiteUrl = (domain) => {
  const d = String(domain || "").trim();
  if (!d || d === "—") return null;
  if (/^https?:\/\//i.test(d)) return d;
  return `https://${d.replace(/^\/+/, "")}`;
};

const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "live" || s === "active") {
    return { color: crmColors.success.main, bg: alpha(crmColors.success.main, 0.12) };
  }
  if (s === "draft" || s === "inactive") {
    return { color: crmColors.warning.dark, bg: alpha(crmColors.warning.main, 0.14) };
  }
  if (s.includes("domain")) {
    return { color: BRAND, bg: alpha(BRAND, 0.12) };
  }
  return { color: crmColors.text.secondary, bg: alpha("#94a3b8", 0.14) };
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fff",
    transition: "box-shadow 0.15s ease, border-color 0.15s ease",
    "&:hover": { bgcolor: alpha(BRAND, 0.015) },
    "&.Mui-focused": {
      boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}`,
    },
  },
  "& .MuiInputLabel-root": { fontWeight: 600, fontSize: "0.82rem" },
};

const Field = ({ label, value, onChange, multiline = false, rows = 3, disabled = false }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    multiline={multiline}
    minRows={multiline ? rows : undefined}
    disabled={disabled}
    sx={fieldSx}
  />
);

const GroupLabel = ({ icon, children, color = BRAND }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25, mt: 0.25 }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "8px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: alpha(color, 0.12),
        color,
        "& .MuiSvgIcon-root": { fontSize: 15 },
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: "0.72rem",
        fontWeight: 800,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: crmColors.text.secondary,
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const Section = ({ title, subtitle, icon, accent = BRAND, actions, children, bodySx }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(accent, 0.14)}`,
      overflow: "hidden",
      bgcolor: "#fff",
      boxShadow: `0 10px 28px ${alpha(accent, 0.06)}`,
    }}
  >
    <Box
      sx={{
        px: 2.25,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.25,
        borderBottom: `1px solid ${alpha(accent, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, ${alpha(accent, 0.02)} 55%, #fff 100%)`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        {icon && (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: accent,
              color: "#fff",
              boxShadow: `0 6px 14px ${alpha(accent, 0.35)}`,
            }}
          >
            {icon}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: crmColors.text.primary, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: crmColors.text.secondary, display: "block", mt: 0.15 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions}
    </Box>
    <Box sx={{ p: { xs: 1.75, md: 2.25 }, ...bodySx }}>{children}</Box>
  </Paper>
);

const ManageWebsiteSchoolDetail = () => {
  const { schoolCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [activeModule, setActiveModule] = useState("about");
  const [moduleRows, setModuleRows] = useState([]);
  const [moduleLoading, setModuleLoading] = useState(false);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const hydrateForm = useCallback((data) => {
    const school = data?.school || {};
    const details = data?.details || {};
    const about = data?.about || {};
    setForm({
      ...emptyForm(),
      school_name: school.school_name || "",
      school_address: school.school_address || "",
      country: school.country || "",
      state: school.state || "",
      admin_name: school.admin_name || "",
      admin_phone: school.admin_phone || "",
      domain_name: school.domain_name || "",
      status: school.status || "Active",
      school_tag_line: details.school_tag_line || school.school_tag_line || "",
      school_heading: details.school_heading || school.school_heading || "",
      school_estb_year: details.school_estb_year || "",
      affiliated_to: details.affiliated_to || school.affiliated_to || "",
      school_reg_no: details.school_reg_no || "",
      affi_no: details.affi_no || details.Affi_no || "",
      website: details.website || school.details_website || "",
      official_contact_no1:
        details.official_contact_no1 ||
        details.officail_contact_no1 ||
        school.official_contact_no1 ||
        school.officail_contact_no1 ||
        "",
      official_contact_no2:
        details.official_contact_no2 ||
        details.officail_contact_no2 ||
        school.official_contact_no2 ||
        "",
      official_email:
        details.official_email ||
        details.officail_email ||
        school.official_email ||
        school.officail_email ||
        "",
      principal_name: details.principal_name || school.principal_name || "",
      principal_qualification: details.principal_qualification || "",
      about_title: about.about_title || "",
      about_heading: about.about_heading || "",
      about_school: about.about_school || "",
      mission_title: about.mission_title || "",
      mission_heading: about.mission_heading || "",
      school_mission: about.school_mission || about.mission_statement || "",
      vision_title: about.vision_title || "",
      vision_heading: about.vision_heading || "",
      school_vision: about.school_vision || "",
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await websiteApiService.getWebsiteSchoolDetail(schoolCode);
    if (result.success && result.data) {
      const data = result.data?.school ? result.data : result.data?.data || result.data;
      setPayload(data);
      hydrateForm(data);
    } else {
      toast.error(result.error || "Failed to load school website detail");
      setPayload(null);
    }
    setLoading(false);
  }, [schoolCode, hydrateForm]);

  const loadModule = useCallback(
    async (moduleId) => {
      if (!schoolCode || !moduleId) return;
      setModuleLoading(true);
      const result = await websiteApiService.getWebsiteModule(schoolCode, moduleId);
      if (result.success) {
        const rows = result.data?.rows || result.data?.data?.rows || [];
        setModuleRows(Array.isArray(rows) ? rows : []);
      } else {
        setModuleRows([]);
        toast.error(result.error || "Failed to load module");
      }
      setModuleLoading(false);
    },
    [schoolCode]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (payload?.school?.school_code) {
      loadModule(activeModule);
    }
  }, [payload, activeModule, loadModule]);

  const school = payload?.school;
  const moduleCounts = payload?.module_counts || {};
  const moduleList = useMemo(() => {
    const catalog = payload?.modules || Object.keys(MODULE_META).map((id) => ({ id }));
    return catalog.filter((m) => MODULE_META[m.id]);
  }, [payload]);

  const previewColumns = useMemo(() => {
    if (!moduleRows.length) return [];
    const keys = Object.keys(moduleRows[0]).filter(
      (k) => !["school_code"].includes(k) && typeof moduleRows[0][k] !== "object"
    );
    return keys.slice(0, 6);
  }, [moduleRows]);

  const contentTotal = useMemo(
    () => Object.values(moduleCounts).reduce((sum, n) => sum + (Number(n) || 0), 0),
    [moduleCounts]
  );

  const handleSave = async () => {
    if (!form.school_name.trim()) {
      toast.error("School name is required");
      return;
    }
    setSaving(true);
    const result = await websiteApiService.updateWebsiteSchoolDetails({
      school_code: schoolCode,
      ...form,
    });
    setSaving(false);
    if (result.success) {
      toast.success("Details updated");
      const data = result.data?.school ? result.data : result.data;
      if (data?.school) {
        setPayload(data);
        hydrateForm(data);
      } else {
        load();
      }
    } else {
      toast.error(result.error || "Update failed");
    }
  };

  const openPublic = () => {
    const url = publicSiteUrl(form.domain_name || school?.domain_name);
    if (!url) {
      toast.error("Domain not configured");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const siteMeta = statusTone(school?.site_status || school?.status);
  const activeMeta = MODULE_META[activeModule] || { label: activeModule, icon: SchoolIcon, color: BRAND };
  const ActiveIcon = activeMeta.icon;

  const headerBtn = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 1.5,
  };

  return (
    <PageContentLayout
      title={school?.school_name || "School website"}
      subtitle={
        school
          ? `${school.school_code}${school.eduok_school_code ? ` · EduOK ${school.eduok_school_code}` : ""}`
          : "Website content & details"
      }
      darkBar
      barSx={{ minHeight: 68, py: 1.75, px: 2.75 }}
      contentPadding={2}
      breadcrumbs={[
        {
          label: "Manage Website",
          icon: <WebsiteIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
        { label: school?.school_name || schoolCode || "Details" },
      ]}
      backTo="/manage-website"
      actions={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Tooltip title="Open public site">
            <span>
              <IconButton
                size="small"
                onClick={openPublic}
                disabled={!publicSiteUrl(form.domain_name || school?.domain_name)}
                sx={{
                  color: "#fff",
                  border: `1px solid ${alpha("#fff", 0.35)}`,
                  borderRadius: "10px",
                  "&:hover": { bgcolor: alpha("#fff", 0.12) },
                }}
              >
                <OpenIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading || saving}
            sx={{
              ...headerBtn,
              color: "#fff",
              borderColor: alpha("#fff", 0.45),
              "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
            }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={saving ? <CircularProgress size={14} sx={{ color: BRAND }} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading || saving || !school}
            sx={{
              ...headerBtn,
              fontWeight: 800,
              bgcolor: "#fff",
              color: BRAND,
              boxShadow: `0 6px 16px ${alpha("#000", 0.12)}`,
              "&:hover": { bgcolor: alpha("#fff", 0.92) },
            }}
          >
            Save changes
          </Button>
        </Stack>
      }
    >
      {loading && !school ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: BRAND }} />
        </Box>
      ) : !school ? (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
            borderRadius: 3,
            border: `1px dashed ${alpha(BRAND, 0.3)}`,
            bgcolor: alpha(BRAND, 0.03),
          }}
        >
          <Avatar sx={{ width: 56, height: 56, bgcolor: alpha(BRAND, 0.12), color: BRAND, mx: "auto", mb: 1.5 }}>
            <SchoolIcon />
          </Avatar>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>School not found</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This school code is not in the website database.
          </Typography>
          <Button variant="contained" disableElevation onClick={() => navigate("/manage-website")} sx={{ textTransform: "none", fontWeight: 700 }}>
            Back to list
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {/* Hero profile */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(BRAND, 0.16)}`,
              boxShadow: `0 14px 36px ${alpha(BRAND, 0.08)}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 2.75 },
                py: { xs: 2, md: 2.5 },
                background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 42%, ${crmColors.hero.end} 100%)`,
                color: "#fff",
                position: "relative",
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  right: -60,
                  top: -70,
                  bgcolor: alpha("#fff", 0.08),
                },
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                sx={{ position: "relative", zIndex: 1 }}
              >
                <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: alpha("#fff", 0.18),
                      color: "#fff",
                      border: `2px solid ${alpha("#fff", 0.45)}`,
                      fontWeight: 800,
                      fontSize: "1.35rem",
                    }}
                  >
                    {(form.school_name || "S").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", md: "1.35rem" }, lineHeight: 1.2 }}>
                      {form.school_name || school.school_name}
                    </Typography>
                    <Typography sx={{ opacity: 0.85, fontSize: "0.82rem", mt: 0.35, fontWeight: 600 }}>
                      {school.school_code}
                      {school.eduok_school_code ? ` · EduOK ${school.eduok_school_code}` : ""}
                      {form.domain_name ? ` · ${form.domain_name}` : ""}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        label={school.site_status || school.status || "—"}
                        sx={{
                          height: 24,
                          fontWeight: 800,
                          bgcolor: alpha("#fff", 0.18),
                          color: "#fff",
                          border: `1px solid ${alpha("#fff", 0.28)}`,
                        }}
                      />
                      {form.school_tag_line && (
                        <Chip
                          size="small"
                          label={form.school_tag_line}
                          sx={{
                            height: 24,
                            maxWidth: 280,
                            fontWeight: 600,
                            bgcolor: alpha("#fff", 0.12),
                            color: "#fff",
                            "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                  {[
                    { label: "Content items", value: contentTotal },
                    { label: "Banners", value: moduleCounts.banners ?? 0 },
                    { label: "Gallery", value: moduleCounts.gallery ?? 0 },
                    { label: "Enquiries", value: moduleCounts.enquiry ?? 0 },
                  ].map((stat) => (
                    <Box
                      key={stat.label}
                      sx={{
                        minWidth: 88,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: alpha("#fff", 0.14),
                        border: `1px solid ${alpha("#fff", 0.2)}`,
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {stat.label}
                      </Typography>
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, lineHeight: 1.15 }}>{stat.value}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Box>
          </Paper>

          {/* Module tiles */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>Website modules</Typography>
              <Chip
                size="small"
                label={`${moduleList.length} areas`}
                sx={{ height: 22, fontWeight: 700, bgcolor: alpha(BRAND, 0.1), color: BRAND }}
              />
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                  lg: "repeat(7, minmax(0, 1fr))",
                },
                gap: 1.15,
              }}
            >
              {moduleList.map((mod) => {
                const meta = MODULE_META[mod.id] || { label: mod.label || mod.id, icon: SchoolIcon, color: BRAND };
                const Icon = meta.icon;
                const count = moduleCounts[mod.id] ?? 0;
                const selected = activeModule === mod.id;
                return (
                  <Paper
                    key={mod.id}
                    elevation={0}
                    onClick={() => setActiveModule(mod.id)}
                    sx={{
                      p: 1.35,
                      borderRadius: 2.25,
                      cursor: "pointer",
                      border: `1.5px solid ${selected ? meta.color : alpha(meta.color, 0.16)}`,
                      background: selected
                        ? `linear-gradient(160deg, ${alpha(meta.color, 0.16)} 0%, #fff 70%)`
                        : `linear-gradient(160deg, ${alpha(meta.color, 0.06)} 0%, #fff 100%)`,
                      boxShadow: selected ? `0 8px 18px ${alpha(meta.color, 0.14)}` : "none",
                      transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        borderColor: meta.color,
                        boxShadow: `0 10px 20px ${alpha(meta.color, 0.12)}`,
                      },
                    }}
                  >
                    <Stack spacing={0.85}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "11px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: selected ? meta.color : alpha(meta.color, 0.12),
                          color: selected ? "#fff" : meta.color,
                          boxShadow: selected ? `0 4px 10px ${alpha(meta.color, 0.35)}` : "none",
                        }}
                      >
                        <Icon sx={{ fontSize: 17 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", lineHeight: 1.1 }}>
                          {meta.label}
                        </Typography>
                        <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: crmColors.text.primary, lineHeight: 1.15, mt: 0.2 }}>
                          {count}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          </Box>

          {/* General information */}
          <Section
            title="General information"
            subtitle="School identity, contacts & affiliation"
            icon={<InfoIcon sx={{ fontSize: 20 }} />}
            accent={BRAND}
            actions={
              <Chip
                size="small"
                label={school.site_status || school.status || "—"}
                sx={{ fontWeight: 800, height: 26, bgcolor: siteMeta.bg, color: siteMeta.color }}
              />
            }
          >
            <GroupLabel icon={<SchoolIcon />} color={BRAND}>
              Identity
            </GroupLabel>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Field label="School name" value={form.school_name} onChange={(v) => setField("school_name", v)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Field label="School code" value={school.school_code} onChange={() => {}} disabled />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status || "Active"}
                    onChange={(e) => setField("status", e.target.value)}
                    sx={{ borderRadius: "12px", bgcolor: "#fff" }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="Domain name" value={form.domain_name} onChange={(v) => setField("domain_name", v)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="Website URL" value={form.website} onChange={(v) => setField("website", v)} />
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="Address"
                  value={form.school_address}
                  onChange={(v) => setField("school_address", v)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field label="Country" value={form.country} onChange={(v) => setField("country", v)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field label="State" value={form.state} onChange={(v) => setField("state", v)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Established year"
                  value={form.school_estb_year}
                  onChange={(v) => setField("school_estb_year", v)}
                />
              </Grid>
            </Grid>

            <GroupLabel icon={<PhoneIcon />} color="#0d9488">
              Contacts
            </GroupLabel>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Field label="Admin name" value={form.admin_name} onChange={(v) => setField("admin_name", v)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="Admin phone" value={form.admin_phone} onChange={(v) => setField("admin_phone", v)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Official contact 1"
                  value={form.official_contact_no1}
                  onChange={(v) => setField("official_contact_no1", v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Official contact 2"
                  value={form.official_contact_no2}
                  onChange={(v) => setField("official_contact_no2", v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Official email"
                  value={form.official_email}
                  onChange={(v) => setField("official_email", v)}
                />
              </Grid>
            </Grid>

            <GroupLabel icon={<BadgeIcon />} color={crmColors.energy.main}>
              Branding & affiliation
            </GroupLabel>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Field label="Tag line" value={form.school_tag_line} onChange={(v) => setField("school_tag_line", v)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="Heading" value={form.school_heading} onChange={(v) => setField("school_heading", v)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Affiliated to"
                  value={form.affiliated_to}
                  onChange={(v) => setField("affiliated_to", v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field label="Affiliation no." value={form.affi_no} onChange={(v) => setField("affi_no", v)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Registration no."
                  value={form.school_reg_no}
                  onChange={(v) => setField("school_reg_no", v)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="Principal name"
                  value={form.principal_name}
                  onChange={(v) => setField("principal_name", v)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="Principal qualification"
                  value={form.principal_qualification}
                  onChange={(v) => setField("principal_qualification", v)}
                />
              </Grid>
            </Grid>
          </Section>

          {/* About / Mission / Vision */}
          <Section
            title="About · Mission · Vision"
            subtitle="Public website story content"
            icon={<AboutIcon sx={{ fontSize: 20 }} />}
            accent="#7c3aed"
          >
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} md={6}>
                <Field label="About title" value={form.about_title} onChange={(v) => setField("about_title", v)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="About heading"
                  value={form.about_heading}
                  onChange={(v) => setField("about_heading", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="About school"
                  value={form.about_school}
                  onChange={(v) => setField("about_school", v)}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.75}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 1.75,
                    height: "100%",
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(crmColors.success.main, 0.22)}`,
                    background: `linear-gradient(165deg, ${alpha(crmColors.success.main, 0.08)} 0%, #fff 70%)`,
                  }}
                >
                  <GroupLabel icon={<MissionIcon />} color={crmColors.success.main}>
                    Mission
                  </GroupLabel>
                  <Stack spacing={1.35}>
                    <Field
                      label="Mission title"
                      value={form.mission_title}
                      onChange={(v) => setField("mission_title", v)}
                    />
                    <Field
                      label="Mission heading"
                      value={form.mission_heading}
                      onChange={(v) => setField("mission_heading", v)}
                    />
                    <Field
                      label="School mission"
                      value={form.school_mission}
                      onChange={(v) => setField("school_mission", v)}
                      multiline
                      rows={4}
                    />
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 1.75,
                    height: "100%",
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(BRAND, 0.22)}`,
                    background: `linear-gradient(165deg, ${alpha(BRAND, 0.08)} 0%, #fff 70%)`,
                  }}
                >
                  <GroupLabel icon={<VisionIcon />} color={BRAND}>
                    Vision
                  </GroupLabel>
                  <Stack spacing={1.35}>
                    <Field
                      label="Vision title"
                      value={form.vision_title}
                      onChange={(v) => setField("vision_title", v)}
                    />
                    <Field
                      label="Vision heading"
                      value={form.vision_heading}
                      onChange={(v) => setField("vision_heading", v)}
                    />
                    <Field
                      label="School vision"
                      value={form.school_vision}
                      onChange={(v) => setField("school_vision", v)}
                      multiline
                      rows={4}
                    />
                  </Stack>
                </Box>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.25 }}>
              <Button
                variant="contained"
                disableElevation
                startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "11px",
                  px: 2.25,
                  py: 1,
                  bgcolor: BRAND,
                  boxShadow: `0 8px 18px ${alpha(BRAND, 0.28)}`,
                }}
              >
                Save all changes
              </Button>
            </Stack>
          </Section>

          {/* Content preview */}
          <Section
            title={`Content preview · ${activeMeta.label}`}
            subtitle="Select a module tile above to browse live website rows"
            icon={<ActiveIcon sx={{ fontSize: 20 }} />}
            accent={activeMeta.color}
            actions={
              <Chip
                size="small"
                label={`${moduleRows.length} row${moduleRows.length === 1 ? "" : "s"}`}
                sx={{
                  height: 26,
                  fontWeight: 800,
                  bgcolor: alpha(activeMeta.color, 0.12),
                  color: activeMeta.color,
                }}
              />
            }
            bodySx={{ pt: 0, px: 0, pb: 0 }}
          >
            {moduleLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} sx={{ color: activeMeta.color }} />
              </Box>
            ) : moduleRows.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    mx: "auto",
                    mb: 1.25,
                    bgcolor: alpha(activeMeta.color, 0.12),
                    color: activeMeta.color,
                  }}
                >
                  <ActiveIcon />
                </Avatar>
                <Typography sx={{ fontWeight: 700, mb: 0.35 }}>No content yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  This school has no {activeMeta.label.toLowerCase()} rows in the website database.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {previewColumns.map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.68rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "#64748b",
                            bgcolor: alpha(activeMeta.color, 0.07),
                            borderBottom: `1px solid ${alpha(activeMeta.color, 0.14)}`,
                            py: 1.15,
                          }}
                        >
                          {col.replace(/_/g, " ")}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {moduleRows.slice(0, 50).map((row, idx) => (
                      <TableRow
                        key={row.id || idx}
                        hover
                        sx={{
                          "&:nth-of-type(even)": { bgcolor: alpha(activeMeta.color, 0.025) },
                          "& td": { borderBottom: `1px solid ${alpha("#94a3b8", 0.18)}` },
                        }}
                      >
                        {previewColumns.map((col) => (
                          <TableCell key={col} sx={{ fontSize: "0.8rem", maxWidth: 240, py: 1.15 }}>
                            <Typography
                              noWrap
                              title={String(row[col] ?? "")}
                              sx={{ fontSize: "0.8rem", fontWeight: 560, color: crmColors.text.primary }}
                            >
                              {row[col] == null || row[col] === ""
                                ? "—"
                                : String(row[col]).length > 80
                                  ? `${String(row[col]).slice(0, 80)}…`
                                  : String(row[col])}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Section>

          {/* Quick domain hint */}
          {!form.domain_name && (
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",
                border: `1px solid ${alpha(crmColors.warning.main, 0.28)}`,
                bgcolor: alpha(crmColors.warning.main, 0.06),
              }}
            >
              <DomainIcon sx={{ color: crmColors.warning.dark, mt: 0.15 }} />
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", color: crmColors.warning.dark }}>
                  Domain pending
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add a domain above and save so the public site link becomes available.
                </Typography>
              </Box>
            </Paper>
          )}
        </Stack>
      )}
    </PageContentLayout>
  );
};

export default ManageWebsiteSchoolDetail;
